
export function renderPredictive () {
  const colorMap = {
    cyan: {
      badge: "bg-cyan-500/10 border-cyan-400/30",
      icon: "text-cyan-400",
    },
    purple: {
      badge: "bg-purple-500/10 border-purple-400/30",
      icon: "text-purple-400",
    },
    orange: {
      badge: "bg-orange-500/10 border-orange-400/30",
      icon: "text-orange-400",
    },
  };

  return (
    <div className="relative overflow-x-hidden text-slate-900 dark:text-slate-100 font-display flex flex-col transition-colors duration-300">
      <main className="py-10" id="main-content">
        <div className="relative">
          {/* Background glow blobs: hidden on mobile to prevent horizontal scroll */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 hidden md:block w-[800px] h-[600px] bg-netraa-purple/10 rounded-full blur-[120px] pointer-events-none -z-10" />
          <div className="absolute top-40 right-0 hidden md:block w-[600px] h-[500px] bg-netraa-cyan/5 rounded-full blur-[100px] pointer-events-none -z-10" />

          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-6 font-mono px-4 sm:px-0">
            <span>Platform</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span>Netraa AIOps</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="text-primary font-bold">Predictive Modeling with Seasonality</span>
          </div>

          <section className="mb-24 relative" id="predictive-overview">
            <div className="mb-12 px-4 sm:px-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600 leading-tight">
                Advanced Predictive Modelling:<br />
                <span className="text-black dark:text-white">Proactive Reliability Planning</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-text-secondary dark:text-white mx-auto leading-relaxed">
                Move beyond basic linear forecasting. Netraa incorporates seasonality, long-term trends, and historical behavior to predict resource exhaustion with financial-grade confidence.
              </p>
            </div>

            <div
              className="w-full h-[380px] sm:h-[420px] md:h-[450px] relative flex items-end justify-center overflow-hidden
                bg-white dark:bg-[#030303]
                border border-slate-200 dark:border-border-dark
                rounded-2xl p-4 sm:p-6 md:p-8 shadow-md dark:shadow-none transition-colors"
            >
              {/* Grid Background */}
              <div className="absolute inset-0 opacity-20 dark:opacity-30 chart-grid pointer-events-none" />

              {/* Y Axis */}
              <div className="absolute left-2 sm:left-4 top-4 bottom-4 flex flex-col justify-between text-[9px] sm:text-[10px] text-slate-500 dark:text-text-secondary font-mono pointer-events-none">
                <span>100% Saturation</span>
                <span>75%</span>
                <span>50%</span>
                <span>25%</span>
                <span>0%</span>
              </div>

              {/* X Axis */}
              <div className="absolute left-8 sm:left-12 right-2 sm:right-4 bottom-2 flex justify-between text-[9px] sm:text-[10px] text-slate-500 dark:text-text-secondary font-mono whitespace-nowrap pointer-events-none">
                <span>-7 Days</span>
                <span>-3 Days</span>
                <span>NOW</span>
                <span>+2 Days</span>
                <span>+7 Days</span>
              </div>

              {/* Chart Area */}
              <div className="relative w-full h-[90%] border-l border-b border-slate-300 dark:border-white/10 overflow-hidden">
                <svg
                  className="absolute inset-0 w-full h-full overflow-hidden"
                  preserveAspectRatio="none"
                  viewBox="0 0 100 100"
                >
                  {/* Historical Curve */}
                  <path
                    d="M0,78
                       C10,72 15,62 22,66
                       C28,70 34,55 40,60
                       C45,65 48,52 50,48"
                    fill="none"
                    className="stroke-[3px] stroke-cyan-500 dark:stroke-netraa-cyan drop-shadow-[0_0_6px_rgba(0,229,255,0.6)]"
                  />

                  {/* Predicted Curve */}
                  <path
                    d="M50,48
                       C58,44 65,38 75,30
                       C85,22 92,12 100,8"
                    fill="none"
                    className="stroke-[2.5px] stroke-purple-500 dark:stroke-netraa-purple"
                  />

                  {/* Risk Fill Area */}
                  <path
                    d="M50,48
                       C58,44 65,38 75,30
                       C85,22 92,12 100,8
                       L100,28
                       C92,35 85,45 75,52
                       C65,58 58,54 50,48 Z"
                    className="fill-purple-400/20 dark:fill-netraa-purple/10"
                  />

                  {/* Current Point */}
                  <circle
                    cx="50"
                    cy="48"
                    r="4"
                    className="fill-white dark:fill-white stroke-cyan-500 dark:stroke-netraa-cyan stroke-[3]"
                  />

                  {/* Future Risk Node */}
                  <circle
                    cx="88"
                    cy="15"
                    r="6"
                    className="fill-orange-500 dark:fill-orange-500 stroke-white stroke-2 animate-pulse"
                  />

                  {/* Vertical Guide */}
                  <line
                    x1="50"
                    x2="50"
                    y1="0"
                    y2="100"
                    className="stroke-slate-300 dark:stroke-white/20 stroke-dashed"
                  />
                </svg>

                {/* Risk Tooltip */}
                <div className="absolute top-[18%] left-[58%] sm:left-[70%] bg-white dark:bg-bg-card border border-orange-400/40 p-3 sm:p-4 rounded-xl shadow-lg dark:shadow-glow-orange backdrop-blur-md z-20 max-w-[220px] sm:max-w-none">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-orange-500 dark:text-orange-500 text-sm">
                      warning
                    </span>
                    <span className="text-[10px] sm:text-xs font-bold text-orange-500 dark:text-orange-500 uppercase">
                      Predicted Risk Zone
                    </span>
                  </div>

                  <div className="text-xs sm:text-sm font-mono text-orange-500">
                    94% Saturation in 48h
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-24 scroll-mt-24 px-4 sm:px-0" id="reactive-chaos">
            <div className="mx-auto px-0 sm:px-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-12 flex items-center gap-3 text-slate-900 dark:text-white leading-tight">
                <span className="material-symbols-outlined text-netraa-orange text-4xl">
                  warning
                </span>
                The Problem:
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-500">
                  Reactive Operations & Blind Spots
                </span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <p className="text-base sm:text-lg text-slate-600 dark:text-white leading-relaxed mb-6">
                    Basic tools like Datadog lack seasonality, and Dynatrace focuses only on current anomalies.
                  </p>

                  <p className="text-base sm:text-lg text-slate-600 dark:text-white leading-relaxed border-l-2 border-netraa-orange pl-4">
                    The result? You are always reacting to outages instead of preventing them, leading to{" "}
                    <span className="text-slate-900 dark:text-white font-medium">
                      budget overruns
                    </span>{" "}
                    and performance degradation.
                  </p>
                </div>

                <div
                  className="relative h-[280px] sm:h-[320px] w-full rounded-xl overflow-hidden p-4 sm:p-6 flex items-end
                    bg-white border border-slate-200
                    dark:bg-[#080808] dark:border-white/5"
                >
                  <div className="absolute inset-0 pointer-events-none opacity-20">
                    <div className="absolute top-1/2 left-0 w-full h-px bg-slate-400 dark:bg-white/20" />
                    <div className="absolute left-1/2 top-0 h-full w-px bg-slate-400 dark:bg-white/20" />
                  </div>

                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                    <path
                      d="M5,78 L28,72 L45,68 L63,55 L75,35"
                      className="stroke-slate-400 dark:stroke-[#9CA3AF]"
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                    />

                    <line
                      x1="88"
                      x2="88"
                      y1="0"
                      y2="100"
                      className="stroke-netraa-orange"
                      strokeWidth="5"
                    />

                    <circle
                      cx="88"
                      cy="18"
                      r="3.5"
                      className="fill-red-500 animate-ping"
                    />
                  </svg>

                  <div className="absolute left-[44%] sm:left-[48%] top-[35%] sm:top-[38%] rotate-90 origin-left">
                    <div className="bg-red-500/10 border border-red-500 text-red-500 text-[10px] font-semibold tracking-wider px-2 py-1 rounded whitespace-nowrap">
                      UNEXPECTED OUTAGE
                    </div>
                  </div>

                  <div className="absolute bottom-4 left-4 flex flex-col sm:flex-row gap-3 sm:gap-6 text-xs text-slate-500 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-red-400 text-base">
                        timer_off
                      </span>
                      Downtime
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-orange-400 text-base">
                        local_fire_department
                      </span>
                      Cost Spike
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="future-proof-engine" className="py-16 lg:py-20 scroll-mt-24 px-4 sm:px-0">
            <div className="mx-auto px-0 sm:px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-slate-900 dark:text-white leading-tight">
                  The Solution:{" "}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-500">
                    Multi-Factor Engines for Future-Proofing
                  </span>
                </h2>

                <p className="text-base text-slate-600 dark:text-gray-400 mx-auto">
                  Financial-grade mathematics for your infrastructure.
                </p>
              </div>

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  className="hidden md:block absolute top-1/2 left-0 w-full h-px
                    bg-gradient-to-r from-transparent via-purple-500/40 to-transparent
                    -translate-y-1/2 pointer-events-none"
                />

                {[
                  {
                    icon: "waves",
                    color: "cyan",
                    title: "1. Deep Seasonality",
                    bold: "Pattern Recognition.",
                    text: "Incorporating daily, weekly, and monthly patterns to distinguish normal cycles from genuine growth.",
                  },
                  {
                    icon: "query_stats",
                    color: "purple",
                    title: "2. Confidence & Certainty",
                    bold: "Financial-grade Math.",
                    text: "We calculate confidence intervals based on historical volatility, giving you probability ranges, not guesses.",
                  },
                  {
                    icon: "speed",
                    color: "orange",
                    title: "3. Risk Identification",
                    bold: "Anticipate Exhaustion.",
                    text: "Proactively identify when infrastructure will hit capacity limits before the alert fires.",
                  },
                ].map((card, i) => (
                  <div
                    key={i}
                    className="relative bg-slate-900 dark:bg-[#0b0b0f] border border-white/10 rounded-xl p-7 text-center transition duration-300 hover:-translate-y-2"
                  >
                    <div
                      className={`w-14 h-14 mx-auto mb-4 rounded-full ${colorMap[card.color].badge} flex items-center justify-center`}
                    >
                      <span className={`material-symbols-outlined ${colorMap[card.color].icon} text-2xl`}>
                        {card.icon}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-3">
                      {card.title}
                    </h3>

                    <p className="text-sm text-gray-400 leading-relaxed">
                      <span className="block text-white font-medium mb-1">
                        {card.bold}
                      </span>
                      {card.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      <style>{`
        @layer base {
          body { font-family: 'Red Hat Display', sans-serif; }
        }
        .glass-card { background: linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%); backdrop-filter: blur(12px); border-radius: 1rem; border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 8px 32px 0 rgba(0,0,0,0.5); }
        .neon-text-orange { text-shadow: 0 0 15px rgba(255, 145, 77, 0.6); }
        .neon-text-cyan { text-shadow: 0 0 15px rgba(0, 229, 255, 0.6); }
        .neon-text-purple { text-shadow: 0 0 15px rgba(124, 77, 255, 0.6); }
        .neon-text-gold { text-shadow: 0 0 15px rgba(255, 215, 0, 0.6); }
        .sidebar-scroll::-webkit-scrollbar { width: 5px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: #080808; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #1E1E22; border-radius: 3px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #FF5722; }
        .chart-grid {
          background-image:
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .risk-zone-bg {
          background: repeating-linear-gradient(
            45deg,
            rgba(255,87,34,0.1),
            rgba(255,87,34,0.1) 10px,
            rgba(255,87,34,0.05) 10px,
            rgba(255,87,34,0.05) 20px
          );
        }
        html, body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};